import React, { PropsWithChildren, useEffect, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { useFlag, useFlagsStatus, useUnleashContext } from '@unleash/proxy-client-react';

import { verifyUserSession } from '../api/api-gateway/verifySession';
import { migrateAnonymous } from '../api/simple-project-service/simpleProjectService';
import { FAKE_USER_ID_KEY, FAKE_USER_ID_PREFIX } from '../constants';
import { useGoogleAnalyticsScript } from '../hooks/tracking/useGoogleAnalytics/useGoogleAnalyticsScript';
import { useGoogleTagManagerScript } from '../hooks/tracking/useGoogleTagManager/useGoogleTagManagerScript';
import { useTagCommander } from '../hooks/tracking/useTagCommander/useTagCommander';
import { useApp } from '../hooks/useApp';
import { AsyncState, useAsync } from '../hooks/useAsync';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { LoadingSpinnerPage } from '../pages/LoadingSpinnerPage';
import { ID_TOKEN } from '../providers/AppContext/AppContext';
import { AppActions } from '../providers/AppContext/appActions';
import { useUserProfile } from '../providers/UserProfileContext/UserProfileContext';
import { setupAuthorizationTokenHandler } from '../services/axiosConfig';
import { getLocaleForLanguage } from '../services/locale';
import { useLocalisation } from '../services/localisationContext';

/**
 * This component created to put all one-time initialization logic (also from Pages.tsx)
 */
export const Initialization: React.FC<PropsWithChildren> = ({ children }) => {
  const updateUnleashContext = useUnleashContext();
  const { currentLanguage, currentCountry, changeLocale } = useLocalisation();
  const [selectedCountry, setSelectedCountry] = useLocalStorage<string>('selectedCountry');
  const { flagsReady } = useFlagsStatus();
  const isTagManagerEnabled = useFlag('wsc.use-tag-manager');
  /* inject TagCommander script (cookie banner) */
  const { loadTagCommanderScript } = useTagCommander();
  /* injects (or not) gtag based on the useTagCommander state or inject google tag manager */
  useGoogleAnalyticsScript(flagsReady && !isTagManagerEnabled);
  useGoogleTagManagerScript(flagsReady && isTagManagerEnabled);

  useEffect(() => {
    if (currentCountry && currentLanguage) {
      // use act commander script directly, without google tag manager
      if (flagsReady && !isTagManagerEnabled) {
        loadTagCommanderScript(currentCountry, currentLanguage);
      }
      if (!selectedCountry) {
        setSelectedCountry(currentCountry.toUpperCase());
      }
    }
  }, [
    loadTagCommanderScript,
    currentCountry,
    currentLanguage,
    selectedCountry,
    setSelectedCountry,
    isTagManagerEnabled,
    flagsReady,
  ]);

  const { loggedIn, userId, dispatch: appDispatch } = useApp();

  /** initialize Unleash context */
  useEffect(() => {
    updateUnleashContext({ userId: userId || 'public' });
  }, [userId, updateUnleashContext]);

  const [sessionChecked, setSessionChecked] = useState<boolean>(!loggedIn);
  const [migrationDone, setMigrationDone] = useState<boolean>(false);

  const [localStorageLocale, updateLocalStorageLocale] = useLocalStorage<string>('locale');

  const [fakeUserId, setFakeUserId, removeFakeUserId] = useLocalStorage<string>(FAKE_USER_ID_KEY);
  const [, setToken, removeToken] = useLocalStorage<string>(ID_TOKEN);

  const {
    start: runMigrateAnonymous,
    state: migrationAnonymousState,
    error: migrationAnonymousError,
  } = useAsync(migrateAnonymous);

  // const {
  //   start: fetchProfile,
  //   result: userProfile,
  //   state: fetchProfileState,
  // } = useAsync(getUserProfile);
  const { userData: userProfile } = useUserProfile();

  // initialize axios token handler
  useEffect(() => {
    setupAuthorizationTokenHandler(setToken, removeToken);
  }, [setToken, removeToken]);

  // verify user session
  useEffect(() => {
    const verify = async () => {
      loggedIn && (await verifyUserSession());
      setSessionChecked(true);
    };
    !sessionChecked && verify();
  }, [sessionChecked, loggedIn]);

  // run migration if needed
  useEffect(() => {
    if (loggedIn && fakeUserId) {
      setMigrationDone(false);
      runMigrateAnonymous();
    }
    if (!loggedIn && !fakeUserId) {
      setFakeUserId(`${FAKE_USER_ID_PREFIX}${uuid()}`);
    }
    setMigrationDone(true);
  }, [runMigrateAnonymous, fakeUserId, loggedIn, setFakeUserId]);

  // handle migration response
  useEffect(() => {
    if (migrationAnonymousState === AsyncState.Finished) {
      if (!migrationAnonymousError) {
        removeFakeUserId();
      }
      setMigrationDone(true);
    }
  }, [migrationAnonymousState, migrationAnonymousError, removeFakeUserId]);

  useEffect(() => {
    if (!userProfile) {
      return;
    }
    if (userProfile?.language && !localStorageLocale) {
      const nextLocale = getLocaleForLanguage(userProfile.language.slice(0, 2));
      changeLocale(nextLocale);
      updateLocalStorageLocale(nextLocale);
    }
    if (userProfile.firstName && userProfile.lastName) {
      appDispatch({
        type: AppActions.SET_USERNAME,
        payload: `${userProfile.firstName} ${userProfile.lastName}`,
      });
    }
  }, [userProfile, localStorageLocale, changeLocale, updateLocalStorageLocale, appDispatch]);

  const initializationFinished = sessionChecked && migrationDone;

  return initializationFinished ? (
    <>{children}</>
  ) : (
    <Wrapper>
      <LoadingSpinnerPage />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;
